import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LayoutChangeEvent } from 'react-native';
import { RecyclerListView, RecyclerListViewProps } from 'recyclerlistview';
import { RecyclerListViewState } from 'recyclerlistview/dist/reactnative/core/RecyclerListView';
import { GenericToast, IndexPath, ToastOrientations, useToast } from '@alucio/lux-ui';
import { Notation, PresentedMeta } from '@alucio/aws-beacon-amplify/src/models';
import { useDispatch } from 'src/state/redux';
import { useAppSettings } from 'src/state/context/AppSettings';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import useContentPageData, {
  ContentPageData,
  getSlideContentPageDataTitle,
} from 'src/hooks/useContentPageData/useContentPageData';
import { PresentableModelORM } from 'src/state/context/ContentProvider/ContentProvider';
import {
  isFolderItemORM,
  isPageGroupORM,
  ORMTypes,
  PageGroupORM,
  Presentable,
  PresentablePage,
} from 'src/types/types';
import { SLIDE_ROLL_VARIANT, thumbnailVariantSettings } from './StyleSetting';
import { contentPreviewModalActions } from 'src/state/redux/slice/contentPreviewModal';
import { SidebarOptionsEnum } from 'src/components/ContentPreviewModalV2/SideBar/SidebarProvider';
import { sortGroups } from 'src/utils/groupHelpers';
import {
  canHidePresentationPage,
  getFallbackPresentationPageNumberAfterHide,
  getPagesWithHiddenStateOverrides,
  getSlideRollHiddenSlideMode,
  getSlideRulePages,
} from 'src/utils/hiddenSlides';

export type ExtendedState = {
  activeSlide: PresentablePage,
  presentedMeta: PresentedMeta[],
  notations: Notation[],
  ignoreSelected: boolean,
}

export type RecyclerListViewDataType = {
  presentablePage: PresentablePage,
  title: string,
  showInsertTextBtn: boolean,
}
export interface SlideRollContextType {
  presentableData: RecyclerListViewDataType[],
  presentable: Presentable,
  activeSlide: PresentablePage,
  activeSlideIndex: number,
  contentPageData: ContentPageData[],
  presentedMeta: PresentedMeta[],
  variant: SLIDE_ROLL_VARIANT,
  ignoreSelected: boolean,
  horizontal: boolean,
  itemHeight: number,
  itemWidth: number,
  itemContainerMinWidth: number,
  displayPreviewBtn: boolean,
  displayInsertTextBtn: boolean,
  isCustomDeck: boolean,
  isTablet: boolean,
  addPresentation?: (item: PresentableModelORM, pageNumber?: number) => void,
  /* SCROLLING & LAYOUT */
  scrollViewRef: React.RefObject<RecyclerListView<RecyclerListViewProps, RecyclerListViewState>>,
  containerMarginLeft: number, // recycler list view margin left
  handleContainerLayout: (e: LayoutChangeEvent) => void,
  onSelectPage: (pageNumber: number) => void,
  onToggleHidden?: (page: PresentablePage) => void,
  onInsertTextButtonPressed: (pageNumber: number) => void,
  /* GROUP SELECTION */
  displayGroupSelection: boolean,
  groups: PageGroupORM[],
  selectedGroupIndex: IndexPath,
  selectedGroupValue?: string,
  onGroupSelect: (index: IndexPath | IndexPath[]) => void,
}

export interface ProviderProps {
  children: ReactNode,
  presentable: Presentable,
  activeSlide: PresentablePage,
  presentedMeta?: PresentedMeta[],
  variant?: SLIDE_ROLL_VARIANT,
  ignoreSelected?: boolean,
  horizontal?: boolean,
  itemHeight?: number,
  itemWidth?: number,
  scrollToSelected?: boolean,
  displayPreviewBtn?: boolean,
  displayInsertTextBtn?: boolean,
  displayGroupSelection?: boolean,
  addPresentation?: (item: PresentableModelORM, pageNumber?: number) => void,
  onSelect?: (presentationNumber: number) => void,
  onToggleHidden?: (page: PresentablePage) => void,
  onHiddenStateChange?: (page: PresentablePage, isHidden: boolean) => void,
}

const THUMBNAIL_HORIZONTAL_MARGIN = 24;

export const SlideRollContext = createContext<SlideRollContextType>(null!);
SlideRollContext.displayName = 'SlideRollContext';

const SlideRollStateProvider: React.FC<PropsWithChildren<ProviderProps>> = ({
  children,
  presentable,
  activeSlide,
  presentedMeta = [],
  variant = SLIDE_ROLL_VARIANT.DEFAULT,
  ignoreSelected = false,
  horizontal = false,
  itemHeight = thumbnailVariantSettings[variant].thumbnailHeight,
  itemWidth = thumbnailVariantSettings[variant].thumbnailWidth,
  scrollToSelected = true,
  displayPreviewBtn = false,
  displayInsertTextBtn: _displayInsertTextBtn = false,
  displayGroupSelection: _displayGroupSelection = false,
  addPresentation,
  onSelect,
  onToggleHidden,
  onHiddenStateChange,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { deviceMode } = useAppSettings();
  const enableSlideTextInsertion = useFeatureFlags('BEAC_6389_slide_text_insertion');
  const enableHiddenSlides = useFeatureFlags('BEAC_7666_enable_hidden_slides');
  const {
    showHiddenSlidesInSlideRule,
    enableHiddenSlideToggle,
  } = getSlideRollHiddenSlideMode({
    featureEnabled: enableHiddenSlides,
    canToggleHidden: !!onToggleHidden,
  });
  const { contentPageData } = useContentPageData(
    presentable,
    showHiddenSlidesInSlideRule,
    undefined,
    showHiddenSlidesInSlideRule,
  );
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerMarginLeft, setContainerMarginLeft] = useState<number>(0);
  const [hiddenStateOverrides, setHiddenStateOverrides] = useState<Record<string, boolean>>({});
  const scrollViewRef = useRef<RecyclerListView<RecyclerListViewProps, RecyclerListViewState>>(null);

  const itemContainerMinWidth = useMemo(() => itemWidth + THUMBNAIL_HORIZONTAL_MARGIN, [itemWidth]);
  const isTablet = deviceMode === 'tablet';
  const isCustomDeck = useMemo(() =>
    isFolderItemORM(presentable.orm) && presentable.orm.model.type === ORMTypes.CUSTOM_DECK, [presentable]);
  const displayInsertTextBtn = useMemo(
    () => (
      !!_displayInsertTextBtn && enableSlideTextInsertion && isCustomDeck
    ),
    [_displayInsertTextBtn, isCustomDeck, enableSlideTextInsertion],
  )

  const slideRollPages = useMemo(() => showHiddenSlidesInSlideRule
    // Slide roll needs hidden content pages so users can restore source-hidden slides.
    ? getSlideRulePages({
      pages: presentable.presentablePages,
      contentPages: contentPageData,
    })
    : presentable.presentablePages,
  [contentPageData, presentable.presentablePages, showHiddenSlidesInSlideRule]);
  const displaySlideRollPages = useMemo(() => getPagesWithHiddenStateOverrides({
    pages: slideRollPages,
    hiddenStateOverrides,
  }), [hiddenStateOverrides, slideRollPages]);

  const handleToggleHidden = useCallback((page: PresentablePage) => {
    const nextIsHidden = !page.page.isHidden;
    if (nextIsHidden && !canHidePresentationPage({
      pages: displaySlideRollPages,
      pageToHide: page,
    })) {
      toast.add(
        <GenericToast title="At least one slide must remain visible." status="error" />,
        ToastOrientations.TOP_RIGHT,
      );
      return;
    }

    setHiddenStateOverrides(current => ({
      ...current,
      [page.page.pageId]: nextIsHidden,
    }));
    onToggleHidden?.(page);
    onHiddenStateChange?.(page, nextIsHidden);

    if (nextIsHidden && activeSlide.presentationPageNumber === page.presentationPageNumber) {
      const fallbackPageNumber = getFallbackPresentationPageNumberAfterHide({
        pages: displaySlideRollPages,
        hiddenPresentationPageNumber: page.presentationPageNumber,
      });

      if (fallbackPageNumber) {
        onSelect?.(fallbackPageNumber);
      }
    }
  }, [
    activeSlide.presentationPageNumber,
    displaySlideRollPages,
    onHiddenStateChange,
    onSelect,
    onToggleHidden,
    toast,
  ]);

  const presentableData = useMemo(() => displaySlideRollPages.map(page => {
    const pageContentData = contentPageData[page.presentationPageNumber - 1];
    const title = contentPageData.length > 0
      ? getSlideContentPageDataTitle(page.presentationPageNumber - 1, contentPageData) : '';
    const showInsertTextBtn = displayInsertTextBtn &&
      !!pageContentData?.enableSlideTextInsertion;
    return {
      presentablePage: {
        ...page,
      },
      title,
      showInsertTextBtn,
    }
  }), [
    contentPageData,
    displayInsertTextBtn,
    displaySlideRollPages,
  ]);
  const activeSlideIndex = useMemo(() => {
    const index = presentableData.findIndex(({ presentablePage }) =>
      presentablePage.presentationPageNumber === activeSlide.presentationPageNumber);
    return Math.max(index, 0);
  }, [activeSlide.presentationPageNumber, presentableData]);

  /* GROUP SELECTION START */
  const groups = useMemo(() => [...activeSlide.documentVersionORM.relations.pageGroups]
    .sort(sortGroups()), [activeSlide]);
  const hasPageGroup = useMemo(() => !!activeSlide.documentVersionORM.relations.pageGroups.length, [activeSlide]);
  const displayGroupSelection = useMemo(() =>
    _displayGroupSelection && !isCustomDeck && hasPageGroup && variant === SLIDE_ROLL_VARIANT.MEETINGS, []);

  const selectedGroupIndex: IndexPath = useMemo(() => {
    if (isPageGroupORM(presentable.orm)) {
      const group = presentable.orm.model
      const index = groups.findIndex(g => g.model.id === group?.id)
      return new IndexPath(index + 1)
    }
    return new IndexPath(0)
  }, [presentable.orm]);

  const selectedGroupValue = useMemo(() => {
    if (selectedGroupIndex.row === 0) {
      return 'All slides';
    } else if (groups && groups.length && isPageGroupORM(presentable.orm)) {
      return groups[selectedGroupIndex.row - 1].model.name;
    }
  }, [groups, selectedGroupIndex]);

  const onGroupSelect = useCallback((index: IndexPath | IndexPath[]) => {
    if (!addPresentation || !groups || Array.isArray(IndexPath)) return;
    const { row } = index as IndexPath;
    if (row === 0) addPresentation(activeSlide.documentVersionORM);
    else {
      const group = groups[row - 1];
      addPresentation(group);
    }
  }, [groups, addPresentation]);
  /* GROUP SELECTION END */

  /* FUNCTIONS START */
  const onSelectPage = useCallback((pageNumber: number) => {
    onSelect?.(pageNumber);
  }, [onSelect, scrollToSelected]);

  const onInsertTextButtonPressed = useCallback((pageNumber: number) => {
    onSelectPage(pageNumber)
    dispatch(
      contentPreviewModalActions.setOpenedTab({
        openedTab: SidebarOptionsEnum.textInsertion,
      },
      ));
  }, [onSelectPage]);

  const handleContainerLayout = useCallback((e: LayoutChangeEvent) => {
    const containerWidth = e.nativeEvent.layout.width;
    setContainerWidth(containerWidth);
    const leftMarginSpace = (containerWidth % itemContainerMinWidth) / 2;
    setContainerMarginLeft(leftMarginSpace);
  }, [itemWidth, itemContainerMinWidth]);
  /* FUNCTIONS END */

  useEffect(() => {
    if (scrollToSelected && containerWidth) {
      scrollViewRef.current?.scrollToIndex(activeSlideIndex, true);
    }
  }, [activeSlideIndex, scrollToSelected, containerWidth])

  const context: SlideRollContextType = {
    presentableData,
    presentable,
    activeSlide,
    activeSlideIndex,
    contentPageData,
    presentedMeta,
    variant,
    ignoreSelected,
    horizontal,
    itemHeight,
    itemWidth,
    itemContainerMinWidth,
    displayPreviewBtn,
    displayInsertTextBtn,
    isCustomDeck,
    isTablet,
    addPresentation,
    /* SCROLLING & LAYOUT */
    scrollViewRef,
    containerMarginLeft,
    handleContainerLayout,
    onSelectPage,
    onToggleHidden: enableHiddenSlideToggle ? handleToggleHidden : undefined,
    onInsertTextButtonPressed,
    /* GROUP SELECTION */
    displayGroupSelection,
    groups,
    selectedGroupIndex,
    selectedGroupValue,
    onGroupSelect,
  };

  return (
    <SlideRollContext.Provider value={context}>
      {children}
    </SlideRollContext.Provider>
  );
};

SlideRollStateProvider.displayName = 'SlideRollStateProvider';

export const useSlideRollState = () => {
  const context = useContext(SlideRollContext);
  if (!context) {
    throw new Error('useSlideRollState must be used within the SlideRollStateProvider');
  }
  return context;
};

export default SlideRollStateProvider;
